import { Badge } from '@/components/ui/badge';
import { Sparkles, ShieldCheck } from 'lucide-react';

const TIER_LABEL = { fast_track: 'Fast Track', standard: 'Standard', needs_refinement: 'Needs Refinement' };

// Full portal submission for AI use case items (request_type='ai_use_case').
// Shared between the Approvals review page (pre-approval) and Item Detail.
export default function UseCaseSubmission({ item }) {
  const p = item?.use_case_payload;
  if (item?.request_type !== 'ai_use_case' || !p) return null;
  const submitter = p.submitter || {};
  const proc = p.current_process || {};
  const metrics = typeof p.success_metrics === 'object' && p.success_metrics ? p.success_metrics : {};
  const data = typeof p.data === 'object' && p.data ? p.data : {};
  const kpis = Array.isArray(metrics.kpis) ? metrics.kpis : [];
  const types = Array.isArray(data.types) ? data.types : [];

  const Row = ({ label, children }) =>
    children ? (
      <div>
        <div className="text-[11px] uppercase tracking-wide font-bold text-[#98139C] mb-1">{label}</div>
        <div className="text-sm text-[#30302F] dark:text-white leading-relaxed whitespace-pre-line font-light">{children}</div>
      </div>
    ) : null;

  return (
    <section className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6" data-testid="card-use-case">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles size={16} strokeWidth={1.5} className="text-[#98139C]" />
          <h2 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Use Case Submission</h2>
        </div>
        {item.priority_tier && (
          <Badge variant="outline" className="text-[11px] rounded-none bg-[#f0dcf0] dark:bg-[#98139C]/20 text-[#4F0077] dark:text-[#d87bdb] border-0">
            {TIER_LABEL[item.priority_tier] || item.priority_tier}
          </Badge>
        )}
      </div>
      <div className="space-y-4">
        {item.ai_summary && (
          <div className="bg-[#f0dcf0]/50 dark:bg-[#98139C]/10 border border-[#98139C]/20 rounded-lg p-4 text-sm text-[#30302F] dark:text-white/90 leading-relaxed" data-testid="use-case-ai-summary">
            {item.ai_summary}
          </div>
        )}
        <Row label="Submitter">
          {(submitter.name || submitter.email) &&
            `${submitter.name || ''}${submitter.email ? ` <${submitter.email}>` : ''}`}
        </Row>
        <Row label="Business unit">{item.business_unit}</Row>
        <Row label="Contact person">
          {(item.contact_name || item.contact_email) &&
            `${item.contact_name || ''}${item.contact_email ? ` <${item.contact_email}>` : ''}`}
        </Row>
        <Row label="Current process">
          {proc.description &&
            `${proc.description}${proc.hours_per_year ? `\n~${proc.hours_per_year} hours/year today.` : ''}`}
        </Row>
        <Row label="Success metrics">
          {(metrics.description || kpis.length > 0) &&
            `${metrics.description || ''}${kpis.length ? `\nKPIs: ${kpis.join(', ')}` : ''}`}
        </Row>
        {(types.length > 0 || data.sensitivity_tier || data.description) && (
          <div>
            <div className="text-[11px] uppercase tracking-wide font-bold text-[#98139C] mb-1.5">Data & sensitivity</div>
            <div className="flex flex-wrap items-center gap-2 mb-1.5">
              {data.sensitivity_tier && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" data-testid="sensitivity-tier">
                  <ShieldCheck size={12} /> {data.sensitivity_tier}
                </span>
              )}
              {types.map((t) => (
                <span key={t} className="px-2.5 py-0.5 rounded-full text-[11px] font-medium bg-[#faf8fb] dark:bg-white/10 text-[#30302F] dark:text-white/80 border border-[#e3e3e3] dark:border-white/10">
                  {String(t).replace(/_/g, ' ')}
                </span>
              ))}
            </div>
            {data.description && (
              <div className="text-sm text-[#30302F] dark:text-white leading-relaxed font-light">{data.description}</div>
            )}
          </div>
        )}
        <Row label="Alternatives considered">{p.alternatives_considered}</Row>
      </div>
    </section>
  );
}
